import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient, getBlobApiErrorMessage } from '../../../api/client';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import type { WeightTicketCreateResponse } from '../../../types/weight-ticket';
import {
  Scale,
  Droplets,
  CheckCircle,
  Loader2,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { FieldHeader, FieldTitleRow } from '../../../components/field/FieldPageChrome';
import { useBack } from '../../../hooks/useBack';

type WTStep = 'entry' | 'weights' | 'sign' | 'complete';

const COMMODITIES = [
  { key: 'produced_water', label: 'Produced water', density: 1000 },
  { key: 'fresh_water', label: 'Fresh water', density: 1000 },
  { key: 'brine', label: 'Brine', density: 1180 },
  { key: 'waste_fluid', label: 'Waste fluid', density: 1050 },
] as const;

type CommodityKey = (typeof COMMODITIES)[number]['key'];

function SignatureCanvas({ onSign }: { onSign: (data: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  function getPos(e: React.TouchEvent | React.MouseEvent) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const t = e.touches[0];
      return { x: (t.clientX - rect.left) * scaleX, y: (t.clientY - rect.top) * scaleY };
    }
    return {
      x: ((e as React.MouseEvent).clientX - rect.left) * scaleX,
      y: ((e as React.MouseEvent).clientY - rect.top) * scaleY,
    };
  }

  function startDraw(e: React.TouchEvent | React.MouseEvent) {
    e.preventDefault();
    isDrawing.current = true;
    lastPos.current = getPos(e);
  }

  function draw(e: React.TouchEvent | React.MouseEvent) {
    e.preventDefault();
    if (!isDrawing.current || !lastPos.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }

  function endDraw() {
    if (isDrawing.current) {
      isDrawing.current = false;
      lastPos.current = null;
      const data = canvasRef.current?.toDataURL('image/png') || '';
      onSign(data);
    }
  }

  function clearCanvas() {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, 540, 200);
    onSign('');
  }

  return (
    <div>
      <div className="flex justify-between mb-1.5">
        <p className="text-[10px] font-bold text-fieldiq-muted uppercase">Driver Signature</p>
        <button onClick={clearCanvas} className="text-[11px] text-fieldiq-muted hover:text-white">
          Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        aria-label="Driver signature pad"
        width={540}
        height={200}
        className="w-full h-28 rounded-lg border border-fieldiq-border bg-fieldiq-dark cursor-crosshair touch-none"
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
    </div>
  );
}

export default function DriverWeightTicketScreen() {
  const navigate = useNavigate();
  const goBack = useBack('/driver');

  const { loadId } = useParams<{ loadId: string }>();

  const [step, setStep] = useState<WTStep>('entry');

  // Fetch load data to pre-fill fields
  const { data: loadData, isLoading: loadLoading } = useQuery({
    queryKey: ['load', loadId],
    queryFn: () =>
      apiClient
        .get(`/trucking/loads/${loadId}`)
        .then((r) => r.data?.data ?? r.data ?? null),
    enabled: !!loadId,
  });

  // Step 1 state
  const [commodity, setCommodity] = useState<CommodityKey | ''>('');
  const [source, setSource] = useState('');
  const [destination, setDestination] = useState('');
  const [customer, setCustomer] = useState('');

  // Load-derived IDs (for submit)
  const [truckId, setTruckId] = useState('');
  const [truckUnitNumber, setTruckUnitNumber] = useState('');

  // Step 2 state
  const [tareWeight, setTareWeight] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [scaleRef, setScaleRef] = useState('');

  // Step 3 state
  const [driverName, setDriverName] = useState('');
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Step 4 state
  const [wtRef, setWtRef] = useState('Generating...');
  /**
   * The created ticket's id, for its PDF. The button used to show only when
   * `weight_tickets.pdf_url` was set, and the only thing that ever set it was
   * viewing the PDF, which no longer writes (api#858). So it never showed for
   * a new ticket. It now fetches GET /trucking/weight-tickets/:id/pdf with the
   * session, as the Master BOL does: `window.open` to an authenticated route
   * sends no token.
   */
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Pre-fill from load data
  useEffect(() => {
    if (loadData) {
      setTruckId(loadData.truck_id || '');
      setTruckUnitNumber(loadData.truck_unit_number || '');
      setCustomer(loadData.customer_name || '');
      setSource(loadData.pickup_location || '');
      setDestination(loadData.delivery_location || '');
    }
  }, [loadData]);

  const tare = parseFloat(tareWeight) || 0;
  const gross = parseFloat(grossWeight) || 0;
  const net = Math.max(gross - tare, 0);
  const selectedCommodity = COMMODITIES.find((c) => c.key === commodity);
  const density = selectedCommodity?.density || 1000;
  const volume = net > 0 ? (net / density).toFixed(2) : '0.00';

  const steps: { key: WTStep; label: string }[] = [
    { key: 'entry', label: 'Entry' },
    { key: 'weights', label: 'Weights' },
    { key: 'sign', label: 'Sign' },
    { key: 'complete', label: 'Complete' },
  ];

  const canContinueEntry = commodity !== '' && source.trim() !== '';
  const canContinueWeights = tare > 0 && gross > 0 && gross > tare;
  const canSubmit = driverName.trim() !== '' && signature !== '';

  // The tab is claimed BEFORE the await (a pop-up blocker refuses one opened
  // later), then pointed at the PDF fetched with the session's token.
  async function handleDownloadPdf() {
    if (!ticketId) return;
    const win = claimBlobTab();
    if (!win) {
      toast.error(POPUP_BLOCKED_MESSAGE);
      return;
    }
    setPdfLoading(true);
    try {
      const res = await apiClient.get<Blob>(`/trucking/weight-tickets/${ticketId}/pdf`, { responseType: 'blob' });
      showBlobInTab(win, res.data);
    } catch (err) {
      win.close();
      toast.error(await getBlobApiErrorMessage(err, "Couldn't open the weight ticket PDF"));
    } finally {
      setPdfLoading(false);
    }
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      // POST /trucking/weight-tickets reads camelCase ONLY. The snake_case keys
      // this used to send were never read, so every submit 400'd on the route's
      // required-field check and no weight ticket was ever created from here.
      // `job_id` and `logged_at` are dropped: weight_tickets has no job column,
      // and `timestamp` defaults to now(). `net_weight_kg` is a GENERATED
      // column and must not be sent.
      const res = await apiClient.post<WeightTicketCreateResponse>('/trucking/weight-tickets', {
        loadId: loadId || null,
        truckId: truckId || null,
        commodity,
        sourceLsd: source.trim(),
        destination: destination.trim(),
        tareWeightKg: tare,
        grossWeightKg: gross,
        volumeM3: parseFloat(volume),
        scaleTicketRef: scaleRef || null,
        driverName,
        driverSignature: signature,
      });
      // The route returns the inserted row verbatim (`RETURNING *`), so the
      // number is `wt_number`. Falling through to `id` printed a raw UUID at
      // the top of a completed weight ticket.
      const data = res.data?.data ?? res.data;
      setWtRef(data?.wt_number || '');
      setTicketId(data?.id ? String(data.id) : null);
      setStep('complete');
    } catch {
      toast.error('Failed to submit weight ticket');
    }
    setSubmitting(false);
  }

  const inputCls =
    'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

  if (loadId && loadLoading) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      {/* The casing field header and back arrow (fe#815): the Weight ticket tile. */}
      <FieldHeader />
      <FieldTitleRow title={`Weight Ticket${truckUnitNumber ? ` — ${truckUnitNumber}` : ''}`} onBack={goBack} />

      {/* Step tabs */}
      {step !== 'complete' && (
        <div className="max-w-md mx-auto px-4 pt-3">
          <div className="flex gap-1.5">
            {steps.map((s, i) => {
              const stepIdx = steps.findIndex((x) => x.key === step);
              const isActive = s.key === step;
              const isDone = i < stepIdx;
              return (
                <div key={s.key} className="flex-1 text-center">
                  <div
                    className={`h-1 rounded-full mb-1 ${
                      isActive
                        ? 'bg-fieldiq-accent'
                        : isDone
                          ? 'bg-fieldiq-accent/40'
                          : 'bg-fieldiq-border'
                    }`}
                  />
                  <span
                    className={`text-[10px] font-medium ${
                      isActive ? 'text-fieldiq-accent' : 'text-fieldiq-muted'
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-4 space-y-4">
        {/* Step 1 - Entry */}
        {step === 'entry' && (
          <>
            <div>
              <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-2">
                Commodity Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                {COMMODITIES.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setCommodity(c.key)}
                    className={`border rounded-lg px-3 py-2.5 text-sm text-left transition-colors min-h-[48px] ${
                      commodity === c.key
                        ? 'border-fieldiq-accent bg-fieldiq-accent/10 text-white'
                        : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted'
                    }`}
                  >
                    <Droplets
                      size={14}
                      className={`mb-1 ${commodity === c.key ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}
                    />
                    <span className="block text-xs font-medium">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1.5">
                Source Location (LSD)
              </label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="00-00-000-00W0"
                className={inputCls}
              />
            </div>

            <div>
              <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1.5">
                Destination
              </label>
              <input
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="Disposal site or facility"
                className={inputCls}
              />
            </div>

            <div>
              <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1.5">
                Customer
              </label>
              <input
                type="text"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="Customer name"
                className={inputCls}
              />
            </div>

            <button
              disabled={!canContinueEntry}
              onClick={() => setStep('weights')}
              className={`w-full h-12 rounded-xl bg-fieldiq-accent text-fieldiq-dark text-sm font-bold min-h-[48px] ${
                !canContinueEntry ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 2 - Weights */}
        {step === 'weights' && (
          <>
            <div>
              <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1.5">
                Tare Weight (kg)
              </label>
              <input
                type="number"
                inputMode="numeric"
                value={tareWeight}
                onChange={(e) => setTareWeight(e.target.value)}
                placeholder="0"
                className={`${inputCls} text-lg font-semibold`}
              />
            </div>

            <div>
              <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1.5">
                Gross Weight (kg)
              </label>
              <input
                type="number"
                inputMode="numeric"
                value={grossWeight}
                onChange={(e) => setGrossWeight(e.target.value)}
                placeholder="0"
                className={`${inputCls} text-lg font-semibold`}
              />
            </div>

            {/* Net + Volume info */}
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-fieldiq-muted">Net Weight</span>
                <span className="text-sm font-bold text-white">
                  {net.toLocaleString()} kg
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-fieldiq-muted">
                  Volume ({selectedCommodity?.label || 'water'} @ {density} kg/m&sup3;)
                </span>
                <span className="text-sm font-bold text-fieldiq-accent">{volume} m&sup3;</span>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1.5">
                Scale Ticket Ref (optional)
              </label>
              <input
                type="text"
                value={scaleRef}
                onChange={(e) => setScaleRef(e.target.value)}
                placeholder="Ticket #"
                className={inputCls}
              />
            </div>

            <button
              disabled={!canContinueWeights}
              onClick={() => setStep('sign')}
              className={`w-full h-12 rounded-xl bg-fieldiq-accent text-fieldiq-dark text-sm font-bold min-h-[48px] ${
                !canContinueWeights ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Continue
            </button>
          </>
        )}

        {/* Step 3 - Sign */}
        {step === 'sign' && (
          <>
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2">
              <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-2">
                Ticket Summary
              </h3>
              <div className="flex justify-between text-xs">
                <span className="text-fieldiq-muted">Commodity</span>
                <span className="text-white font-medium">{selectedCommodity?.label}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-fieldiq-muted">From</span>
                <span className="text-white font-medium">{source}</span>
              </div>
              {destination && (
                <div className="flex justify-between text-xs">
                  <span className="text-fieldiq-muted">To</span>
                  <span className="text-white font-medium">{destination}</span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-fieldiq-muted">Net Weight</span>
                <span className="text-white font-medium">{net.toLocaleString()} kg</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-fieldiq-muted">Volume</span>
                <span className="text-fieldiq-accent font-medium">{volume} m&sup3;</span>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-fieldiq-muted uppercase tracking-wider block mb-1.5">
                Driver Name
              </label>
              <input
                type="text"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                placeholder="Full name"
                className={inputCls}
              />
            </div>

            <SignatureCanvas onSign={setSignature} />

            <button
              disabled={!canSubmit || submitting}
              onClick={handleSubmit}
              className={`w-full h-12 rounded-xl bg-fieldiq-accent text-fieldiq-dark text-sm font-bold flex items-center justify-center gap-2 min-h-[48px] ${
                !canSubmit || submitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Weight Ticket'
              )}
            </button>
          </>
        )}

        {/* Step 4 - Complete */}
        {step === 'complete' && (
          <div className="flex flex-col items-center text-center pt-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle size={32} className="text-green-400" />
            </div>
            <h2 className="text-lg font-bold text-white mb-1">Weight ticket submitted</h2>
            <p className="text-sm text-fieldiq-muted mb-6">Your ticket has been recorded.</p>

            <div className="flex flex-wrap gap-2 justify-center mb-8">
              <span className="bg-fieldiq-card border border-fieldiq-border rounded-full px-3 py-1 text-xs text-white font-medium">
                {wtRef}
              </span>
              <span className="bg-fieldiq-card border border-fieldiq-border rounded-full px-3 py-1 text-xs text-white font-medium">
                {net.toLocaleString()} kg
              </span>
              <span className="bg-fieldiq-card border border-fieldiq-border rounded-full px-3 py-1 text-xs text-fieldiq-accent font-medium">
                {volume} m&sup3;
              </span>
              <span className="bg-fieldiq-card border border-fieldiq-border rounded-full px-3 py-1 text-xs text-white font-medium">
                {selectedCommodity?.label || ''}
              </span>
            </div>

            <div className="w-full space-y-3">
              {ticketId && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  className="w-full h-12 rounded-xl border border-fieldiq-border bg-fieldiq-card text-white text-sm font-bold flex items-center justify-center gap-2 min-h-[48px]"
                >
                  <Download size={16} />
                  Download ticket PDF
                </button>
              )}
              <button
                // /my-work/today, the driver's home: "/field" is not a route in the
                // /my-work tree and fell through to its catch-all (#815).
                onClick={() => navigate('/my-work/today')}
                className="w-full h-12 rounded-xl bg-fieldiq-accent text-fieldiq-dark text-sm font-bold min-h-[48px]"
              >
                Next haul
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
